import { UserRole } from '@/types/roles';

export interface MenuItem {
  header?: string;
  title?: string;
  icon?: string;
  id?: number;
  to?: string;
  chip?: string;
  chipColor?: string;
  chipBgColor?: string;
  children?: MenuItem[];
  disabled?: boolean;
  subCaption?: string;
  // Rôles autorisés à voir cette entrée. Absent = visible par tous les rôles
  // authentifiés. Le filtrage est appliqué par VerticalSidebar.
  roles?: UserRole[];
}

// Rôles de l'espace de gestion (par organisation).
const MANAGER_ROLES = [UserRole.MANAGER, UserRole.ORGANIZATION_ADMIN];
// Écrans sensibles au niveau organisation (équipe, comptes, facturation,
// sécurité) : réservés à l'admin d'organisation, jamais au manager délégué.
const ORG_ADMIN_ROLES = [UserRole.ORGANIZATION_ADMIN];

const sidebarItem: MenuItem[] = [
  {
    header: 'menu.sections.administration',
    id: 0,
    roles: [UserRole.SUPER_ADMIN],
    children: [
      {
        title: 'menu.dashboard',
        icon: 'shield-star-line-duotone',
        to: '/admin/dashboard',
        roles: [UserRole.SUPER_ADMIN]
      }
    ]
  },
  {
    header: 'menu.sections.dashboards',
    id: 1,
    roles: MANAGER_ROLES,
    children: [
      {
        title: 'menu.dashboard',
        icon: 'screencast-2-line-duotone',
        to: '/manager/dashboard'
      },
      {
        title: 'menu.performanceCenter',
        icon: 'chart-square-line-duotone',
        to: '/manager/performance',
        chip: 'NEW',
        chipColor: 'success',
        chipBgColor: 'lightsuccess'
      }
    ]
  },
  {
    header: 'menu.sections.operations',
    id: 2,
    roles: MANAGER_ROLES,
    children: [
      {
        title: 'menu.bookings',
        icon: 'calendar-mark-line-duotone',
        to: '/manager/reservations'
      },
      {
        title: 'menu.settings.reservationOptions',
        icon: 'checklist-bold-duotone',
        to: '/manager/reservation-options'
      }
    ]
  },
  {
    header: 'menu.sections.checkin',
    id: 3,
    roles: MANAGER_ROLES,
    children: [
      {
        title: 'menu.checkin.scan',
        icon: 'card-search-line-duotone',
        to: '/manager/checkin/scan'
      },
      {
        title: 'menu.checkin.presences',
        icon: 'shield-user-line-duotone',
        to: '/manager/checkin/presences'
      },
      {
        title: 'menu.checkin.history',
        icon: 'document-text-line-duotone',
        to: '/manager/checkin/history'
      },
      {
        // Activation et réglages du scanner : ils vivaient dans la page
        // « Modules », transverse et redondante avec ce menu.
        title: 'menu.settings.title',
        icon: 'settings-minimalistic-line-duotone',
        to: '/manager/checkin/settings'
      }
    ]
  },
  {
    header: 'menu.sections.lakes',
    id: 4,
    roles: MANAGER_ROLES,
    children: [
      {
        title: 'menu.lakes-list',
        icon: 'passport-line-duotone',
        to: '/manager/lakes'
      },
      {
        title: 'menu.fishing-spots',
        icon: 'home-angle-linear',
        to: '/manager/lakes/fishing-spots'
      }
    ]
  },
  {
    header: 'menu.sections.team',
    id: 5,
    roles: ORG_ADMIN_ROLES,
    children: [
      {
        title: 'menu.team',
        icon: 'shield-user-line-duotone',
        to: '/manager/team'
      },
      {
        title: 'menu.users',
        icon: 'user-plus-rounded-line-duotone',
        to: '/manager/users'
      }
    ]
  },
  {
    header: 'menu.sections.marketing',
    id: 6,
    roles: MANAGER_ROLES,
    children: [
      {
        title: 'menu.campaigns',
        icon: 'megaphone-line-duotone',
        to: '/manager/campaigns',
        chip: 'PREMIUM',
        chipColor: 'warning',
        chipBgColor: 'lightwarning'
      }
    ]
  },
  {
    header: 'menu.sections.settings',
    id: 7,
    roles: MANAGER_ROLES,
    children: [
      // Une entrée par section : les six premières étaient auparavant des
      // onglets d'une page unique, donc invisibles depuis le menu — alors que
      // Modules et SMS y figuraient. Deux niveaux de navigation coexistaient
      // sans règle ; tout est désormais au même niveau.
      {
        title: 'pages.settings.tabs.account',
        icon: 'user-circle-line-duotone',
        to: '/manager/settings/account'
      },
      {
        title: 'pages.settings.tabs.notifications',
        icon: 'bell-line-duotone',
        to: '/manager/settings/notifications'
      },
      {
        title: 'pages.settings.tabs.email_branding',
        icon: 'letter-line-duotone',
        to: '/manager/settings/email-branding'
      },
      {
        title: 'menu.smsSettings',
        icon: 'chat-round-line-line-duotone',
        to: '/manager/settings/sms',
        chip: 'PREMIUM',
        chipColor: 'warning',
        chipBgColor: 'lightwarning'
      },
      {
        title: 'pages.settings.tabs.google_reviews',
        icon: 'star-line-duotone',
        to: '/manager/settings/google-reviews'
      },
      {
        title: 'pages.settings.tabs.billing',
        icon: 'card-line-duotone',
        to: '/manager/settings/billing',
        roles: ORG_ADMIN_ROLES
      },
      {
        title: 'pages.settings.tabs.security',
        icon: 'lock-password-line-duotone',
        to: '/manager/settings/security',
        roles: ORG_ADMIN_ROLES
      }
    ]
  },
  {
    header: 'menu.sections.support',
    id: 8,
    roles: MANAGER_ROLES,
    children: [
      {
        title: 'menu.faq',
        icon: 'question-circle-line-duotone',
        to: '/manager/faq'
      }
    ]
  }
];

export default sidebarItem;
